import React, { useEffect, useState } from "react";
import * as SC from "./layout-main.style";
import { Outlet, useNavigate } from "react-router-dom";
import { Avatar, Button, Typography, IconButton } from "@mui/material";
import { MenuItem } from "./layout-main.type";
import { v4 as uuidv4 } from "uuid";
import {
  Logout,
  VerifiedUser,
  Subscriptions,
  Receipt,
  CreditCard,
  Menu as MenuIcon,
  Dashboard,
  BookOnline,
  Settings,
} from "@mui/icons-material";
import { useReadProfileQuery } from "api/endpoints";
import { useReadAclQuery } from "api/endpoints/acl";
import { SidebarAcl } from "store/reducers/acl/acl.type";
import { setRole, setToken } from "store/reducers/auth/auth.reducer";
import { useAppDispatch } from "store/store-hooks";
import { ConfirmationDialogComponent } from "lib/components/ui/confirmation-dialog";

export const LayoutMain = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: profileData } = useReadProfileQuery();
  const { acl } = useReadAclQuery<SidebarAcl>("sidebar");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Confirmation dialog states
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    confirmColor:
      | "primary"
      | "secondary"
      | "error"
      | "success"
      | "warning"
      | "info";
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "تایید",
    confirmColor: "primary",
    onConfirm: () => {},
  });

  useEffect(() => {
    dispatch(setRole({ role: profileData?.role }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData?.role]);

  useEffect(() => {
    if (!profileData) return;

    dispatch(
      setToken({
        userId: profileData?._id,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData]);

  const menuItems: MenuItem[] = [
    {
      sectionTitle: "داشبورد",
      key: "",
      children: [
        {
          label: "اشتراک من",
          key: "my-subscription",
          icon: <Dashboard />,
          acl: acl?.mySubscription,
        },
        {
          label: "خرید اشتراک",
          key: "purchase",
          icon: <CreditCard />,
          acl: acl?.subscriptionPurchase,
        },
        {
          label: "راهنمای استفاده",
          key: "guidance",
          icon: <BookOnline />,
          acl: true, // Available to all users
        },
      ],
    },
    {
      sectionTitle: "مدیریت",
      key: "",
      children: [
        {
          label: "کاربران",
          key: "admin/users",
          icon: <VerifiedUser />,
          acl: acl?.usersList,
        },
        {
          label: "لیست اشتراک‌ها",
          key: "subscriptions",
          icon: <Subscriptions />,
          acl: acl?.subscriptionsList,
        },
        {
          label: "فیش‌های واریزی",
          key: "payment-receipts",
          icon: <Receipt />,
          acl: acl?.paymentReceipts,
        },
        {
          label: "تنظیمات VPN",
          key: "settings/vpn",
          icon: <Settings />,
          acl: acl?.settings || acl?.manualCategories || acl?.vpnAccounts,
        },
      ],
    },
  ];

  const checkIsMenuItemActive = (parentKey: string, key: string) => {
    const currentPathname = window.location.pathname;
    if (key) return currentPathname.includes(`/${parentKey}/${key}`);
    else return currentPathname.includes(`/${parentKey}`);
  };
  const showConfirmationDialog = (
    title: string,
    message: string,
    confirmText: string,
    confirmColor:
      | "primary"
      | "secondary"
      | "error"
      | "success"
      | "warning"
      | "info",
    onConfirm: () => void
  ) => {
    setConfirmationDialog({
      isOpen: true,
      title,
      message,
      confirmText,
      confirmColor,
      onConfirm,
    });
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const handleUserLogout = () => {
    showConfirmationDialog(
      "خروج از حساب کاربری",
      "آیا از خروج از حساب کاربری اطمینان دارید؟",
      "خروج",
      "error",
      () => {
        localStorage.clear();
        window.location.reload();
      }
    );
  };

  const userFullName = profileData ? `${profileData.fullName}` : "";
  const userRole = profileData?.role || "کاربر";

  return (
    <SC.LayoutMain>
      {/* Header */}
      <div className="main-header">
        <div className="brand-section">
          <div className="brand-logo">V</div>
          <Typography className="brand-title">Velora</Typography>
        </div>

        <div className="user-section">
          <div className="user-info">
            <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
              {userFullName.charAt(0)}
            </Avatar>
            <Typography
              variant="body2"
              sx={{ display: { xs: "none", sm: "block" } }}
            >
              {userFullName}
            </Typography>
          </div>

          <IconButton
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            className="logout-btn-mobile"
            sx={{ display: { xs: "flex", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Mobile Logout Button */}
          <IconButton
            onClick={handleUserLogout}
            className="logout-btn-mobile"
            sx={{
              display: { xs: "flex", md: "none" },
              marginLeft: 1,
            }}
            title="خروج از حساب کاربری"
          >
            <Logout />
          </IconButton>
        </div>
      </div>

      {/* Layout Body */}
      <div className="layout-body">
        {/* Sidebar */}
        <div
          className={`main-sidebar ${mobileSidebarOpen ? "mobile-open" : ""}`}
        >
          <div className="header">
            <Avatar className="profile-avatar" sx={{ width: 60, height: 60 }}>
              {userFullName.charAt(0)}
            </Avatar>
            <Typography className="user-name" variant="h6">
              {userFullName}
            </Typography>
            <Typography className="user-role">{userRole}</Typography>
          </div>

          <div className="menu-items">
            {menuItems.map(
              (menuItem) =>
                !!menuItem.children.filter(({ acl }) => acl).length && (
                  <div key={uuidv4()} className="menu-section">
                    {menuItem.sectionTitle && (
                      <Typography className="section-title">
                        {menuItem.sectionTitle}
                      </Typography>
                    )}
                    <ul>
                      {menuItem.children.map(
                        (menuItemLink) =>
                          menuItemLink.acl && (
                            <li
                              onClick={() => {
                                navigate(
                                  `/${menuItem.key ? menuItem.key + "/" : ""}${
                                    menuItemLink.key
                                  }`
                                );
                                setMobileSidebarOpen(false);
                              }}
                              className={`menu-item ${
                                checkIsMenuItemActive(
                                  menuItem.key,
                                  menuItemLink.key
                                )
                                  ? "--active"
                                  : ""
                              }`}
                              key={uuidv4()}
                            >
                              {menuItemLink.icon}
                              <span className="menu-item-link">
                                {menuItemLink.label}
                              </span>
                            </li>
                          )
                      )}
                    </ul>
                  </div>
                )
            )}
          </div>

          <div className="footer">
            <Button
              onClick={handleUserLogout}
              className="logout-btn"
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              <Logout />
              <span>خروج از حساب کاربری</span>
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          <Outlet />
          <div className="spacing" />
        </div>
      </div>

      {/* Mobile Toolbar */}
      <div className="toolbar">
        {menuItems.flatMap((section) =>
          section.children.map((item) => {
            if (item.acl) {
              return (
                <Button
                  key={item.key}
                  onClick={() => {
                    navigate(
                      `/${section.key ? section.key + "/" : ""}${item.key}`
                    );
                  }}
                  className="toolbar-btn"
                  variant="outlined"
                  size="small"
                  sx={{
                    minWidth: "60px",
                    height: "50px",
                    flex: 1,
                    maxWidth: "80px"
                  }}
                >
                  {item.icon}
                </Button>
              );
            } else {
              return null;
            }
          })
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialogComponent
        isOpen={confirmationDialog.isOpen}
        onClose={closeConfirmationDialog}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText={confirmationDialog.confirmText}
        confirmColor={confirmationDialog.confirmColor}
      />
    </SC.LayoutMain>
  );
};
